import * as React from "react";
import { Button, Form, Row, Col, Card } from "react-bootstrap";

import { DataType, FilterOperator } from "@/shared/Enums";
import { AdvancedSearchFilterItem } from "@/shared/models/advanced-search/AdvancedSearchFilterItem";
import { AdvancedSearchFilterNameItem } from "@/shared/models/advanced-search/AdvancedSearchFilterNameItem";
import { Utils } from "@/shared/utils/Utils";
import DropdownInside from "../DropdownInside";

interface IProps {
  fieldNameList: AdvancedSearchFilterNameItem[];
  onFiltersChange?: (filters: AdvancedSearchFilterItem[]) => void;
  defaultFilterList?: AdvancedSearchFilterItem[];
}

export default function AdvancedFilter({ fieldNameList, onFiltersChange, defaultFilterList }: IProps) {
  const [filterItems, setFilterItems] = React.useState<AdvancedSearchFilterItem[]>(defaultFilterList ?? []);

  React.useEffect(() => {
    // Only update if the prop is actually different from current state (ignoring ref changes)
    setFilterItems((prev) => {
      const next = defaultFilterList ?? [];
      const isSame = Utils.areObjectsEqual(prev, next);
      return isSame ? prev : next;
    });
  }, [defaultFilterList]);

  const getOperatorsByType = (type: DataType): { value: number; label: string }[] => {
    switch (type) {
      case DataType.Text:
        return [
          { value: FilterOperator.Contains, label: "Contains" },
          { value: FilterOperator.StartsWith, label: "Starts With" },
          { value: FilterOperator.EndsWith, label: "Ends With" },
          { value: FilterOperator.Equal, label: "Equals" },
          { value: FilterOperator.NotEqual, label: "Not Equals" },
        ];
      case DataType.DropDown:
        return [
          { value: FilterOperator.In, label: "In" },
          { value: FilterOperator.NotIn, label: "Not In" },
        ];
      case DataType.Int:
      case DataType.Decimal:
        return [
          { value: FilterOperator.Equal, label: "Equals" },
          { value: FilterOperator.NotEqual, label: "Not Equals" },
          { value: FilterOperator.GreaterThan, label: "Greater Than" },
          { value: FilterOperator.LessThan, label: "Less Than" },
          { value: FilterOperator.GreaterThanOrEqual, label: "Greater or Equal" },
          { value: FilterOperator.LessThanOrEqual, label: "Less or Equal" },
        ];
      case DataType.Date:
      case DataType.DateTime:
        return [
          { value: FilterOperator.GreaterThan, label: "After" },
          { value: FilterOperator.LessThan, label: "Before" },
        ];
      case DataType.Boolean:
        return [{ value: FilterOperator.Equal, label: "Equals" }];
      default:
        return [
          { value: FilterOperator.Equal, label: "Equals" },
          { value: FilterOperator.NotEqual, label: "Not Equals" },
        ];
    }
  };

  // Add a new filter row
  const addFilter = () => {
    const newFilter: AdvancedSearchFilterItem = { field: "", operator: "", value: "", type: DataType.Text };
    const updatedFilters = [...filterItems, newFilter];
    setFilterItems(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  // Delete a filter row
  const deleteFilter = (index: number) => {
    const updatedFilters = filterItems.filter((_, i) => i !== index);
    setFilterItems(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  // Update a filter item
  const updateFilter = (index: number, field: Exclude<keyof AdvancedSearchFilterItem, "type">, value: string) => {
    const updatedFilters = [...filterItems];
    const currentFilter = { ...updatedFilters[index] };

    if (field === "value") {
      const fieldType = getFieldType(currentFilter.field);
      if (fieldType === DataType.Int) {
        // For integers, only allow digits (and a leading minus sign), or an empty string.
        if (!/^-?\d*$/.test(value)) {
          return; // Do not update state if input is not a valid integer pattern
        }
      }
    }

    currentFilter[field] = value;

    // Reset operator, value and type when field changes
    if (field === "field") {
      currentFilter.operator = "";
      currentFilter.value = "";
      currentFilter.type = getFieldType(value);
    }

    updatedFilters[index] = currentFilter;
    setFilterItems(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  // Get the selected field type
  const getFieldType = (fieldName: string) => {
    const field = fieldNameList.find((f) => f.name === fieldName);
    return field?.type || DataType.Text;
  };

  const getFieldDropDownList = (fieldName: string) => {
    const field = fieldNameList.find((f) => f.name === fieldName);
    return field?.dropDownList || [];
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterItems([]);
    onFiltersChange?.([]);
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Advanced Search Filters</h5>
        <div>
          {filterItems.length > 0 && (
            <Button variant="outline-danger" size="sm" onClick={clearAllFilters} className="me-2">
              <i className="fas fa-trash"></i> Clear All
            </Button>
          )}
          <Button variant="outline-primary" size="sm" onClick={addFilter}>
            <i className="fas fa-plus"></i> Add Filter
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {filterItems.length === 0 ? (
          <div className="text-center text-muted py-2">
            <p>No filters added. Click "Add Filter" to start building your search criteria.</p>
          </div>
        ) : (
          <div className="filter-rows">
            {filterItems.map((filter, index) => {
              const fieldType = getFieldType(filter.field);
              const operators = getOperatorsByType(fieldType);

              return (
                <Row key={index} className="mb-3 align-items-center">
                  {/* Field Name Dropdown */}
                  <Col md={4}>
                    <Form.Select value={filter.field} onChange={(e) => updateFilter(index, "field", e.target.value)}>
                      <option value="">Select Field...</option>
                      {fieldNameList.map((fieldName, idx) => (
                        <option key={idx} value={fieldName.name}>
                          {fieldName.label} ({fieldName.type})
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  {/* Operator Dropdown */}
                  <Col md={3}>
                    <Form.Select
                      value={filter.operator}
                      onChange={(e) => updateFilter(index, "operator", e.target.value)}
                      disabled={!filter.field}
                    >
                      <option value="">Select Operation...</option>
                      {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  {/* Value Input */}
                  <Col md={4}>
                    {fieldType === DataType.Boolean ? (
                      <Form.Select
                        value={filter.value}
                        onChange={(e) => updateFilter(index, "value", e.target.value)}
                        disabled={!filter.operator}
                      >
                        <option value="">Select Value...</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </Form.Select>
                    ) : fieldType === DataType.DropDown ? (
                      <DropdownInside
                        label={"Value"}
                        disabled={!filter.operator}
                        options={getFieldDropDownList(filter.field)}
                        value={filter.value === "" ? [] : filter.value.split(",")}
                        onChange={(value) => updateFilter(index, "value", value.join(","))}
                      ></DropdownInside>
                    ) : fieldType === DataType.Date || fieldType === DataType.DateTime ? (
                      <Form.Control
                        type={fieldType === DataType.Date ? "date" : "datetime-local"}
                        value={filter.value}
                        onChange={(e) => updateFilter(index, "value", e.target.value)}
                        disabled={!filter.operator}
                      />
                    ) : fieldType === DataType.Int || fieldType === DataType.Decimal ? (
                      <Form.Control
                        type="number"
                        step={fieldType === DataType.Decimal ? "0.01" : "1"}
                        value={filter.value}
                        onChange={(e) => updateFilter(index, "value", e.target.value)}
                        placeholder="Enter value..."
                        disabled={!filter.operator}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateFilter(index, "value", e.target.value)}
                        placeholder="Enter value..."
                        disabled={!filter.operator}
                      />
                    )}
                  </Col>

                  {/* Delete Button */}
                  <Col md={1}>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteFilter(index)}
                      title="Delete this filter"
                      className="btn-close"
                      aria-label="Close"
                    />
                  </Col>
                </Row>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
